import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import { ExportButton } from '../components/ExportButton';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Trash2, MapPin, Search, ExternalLink, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../hooks/useSettings';
import { usePermissions } from '../hooks/usePermissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_BADGE = {
  'Canal de WhatsApp': 'bg-blue-100 text-blue-800 border-blue-200',
  'Grupo de WhatsApp': 'bg-green-100 text-green-800 border-green-200',
  'Comunidade de WhatsApp': 'bg-purple-100 text-purple-800 border-purple-200',
  'Link de Atendimento': 'bg-gray-100 text-gray-800 border-gray-200',
};

const STATUS_BADGE = {
  ativo: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  pausado: 'bg-amber-100 text-amber-700 border-amber-200',
  arquivado: 'bg-slate-100 text-slate-700 border-slate-200',
};

export const DestinosPage = () => {
  const { settings } = useSettings();
  const { canEdit } = usePermissions();
  const canEditDestinos = canEdit('destinos');
  const tiposDestino = settings?.destino_tipos || ['Canal de WhatsApp', 'Grupo de WhatsApp', 'Comunidade de WhatsApp', 'Link de Atendimento'];

  const [destinos, setDestinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('todos');
  const [filterStatus, setFilterStatus] = useState('todos');

  const emptyForm = {
    nome: '',
    tipo: 'Canal de WhatsApp',
    url_whatsapp: '',
    status: 'ativo',
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    fetchDestinos();
  }, []);

  const fetchDestinos = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/destinations`, { withCredentials: true });
      setDestinos(data);
    } catch (error) {
      toast.error('Erro ao carregar destinos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.url_whatsapp.trim()) {
      toast.error('Preencha nome e URL do WhatsApp');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/destinations/${editingId}`, formData, { withCredentials: true });
        toast.success('Destino atualizado');
      } else {
        await axios.post(`${BACKEND_URL}/api/destinations`, formData, { withCredentials: true });
        toast.success('Destino criado');
      }
      setDialogOpen(false);
      setEditingId(null);
      setFormData(emptyForm);
      fetchDestinos();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar destino');
    }
  };

  const handleEdit = async (d) => {
    // Need to fetch with URL (only admin sees URL via single GET)
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/destinations/${d.id}`, { withCredentials: true });
      setFormData({
        nome: data.nome,
        tipo: data.tipo,
        url_whatsapp: data.url_whatsapp || '',
        status: data.status,
      });
      setEditingId(d.id);
      setDialogOpen(true);
    } catch (err) {
      toast.error('Erro ao carregar destino para edição');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este destino?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/destinations/${id}`, { withCredentials: true });
      toast.success('Destino excluído');
      fetchDestinos();
    } catch (error) {
      toast.error('Erro ao excluir destino');
    }
  };

  const openNewDialog = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const filtered = useMemo(() => {
    return destinos.filter((d) => {
      if (searchTerm) {
        const t = searchTerm.toLowerCase();
        if (!d.nome.toLowerCase().includes(t)) return false;
      }
      if (filterTipo !== 'todos' && d.tipo !== filterTipo) return false;
      if (filterStatus !== 'todos' && d.status !== filterStatus) return false;
      return true;
    });
  }, [destinos, searchTerm, filterTipo, filterStatus]);

  const kpis = useMemo(() => ({
    total: destinos.length,
    ativos: destinos.filter((d) => d.status === 'ativo').length,
    canais: destinos.filter((d) => d.tipo === 'Canal de WhatsApp').length,
    grupos: destinos.filter((d) => d.tipo === 'Grupo de WhatsApp').length,
  }), [destinos]);

  return (
    <Layout>
      <div className="space-y-6" data-testid="destinos-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-3">
              <MapPin className="h-8 w-8 text-indigo-600" />
              Destinos
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Canais, grupos e links do WhatsApp para onde os apoiadores são direcionados
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton resource="destinations" />
            <Button onClick={openNewDialog} data-testid="create-destino-btn" disabled={!canEditDestinos}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Destino
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Total" value={kpis.total} />
          <Kpi label="Ativos" value={kpis.ativos} color="text-emerald-600" />
          <Kpi label="Canais" value={kpis.canais} color="text-blue-600" />
          <Kpi label="Grupos" value={kpis.grupos} color="text-green-600" />
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar por nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-destinos-input"
                />
              </div>
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger data-testid="filter-tipo-select">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os tipos</SelectItem>
                  {tiposDestino.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status-select">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="pausado">Pausado</SelectItem>
                  <SelectItem value="arquivado">Arquivado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-slate-500">Carregando...</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16">
                <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-medium mb-1">Nenhum destino encontrado</p>
                {destinos.length === 0 && canEditDestinos && (
                  <Button onClick={openNewDialog} className="mt-3">
                    <Plus className="h-4 w-4 mr-2" /> Criar primeiro destino
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4">Nome</th>
                      <th className="py-3 px-4">Tipo</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((d) => (
                      <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`destino-row-${d.id}`}>
                        <td className="py-3 px-4 font-medium text-slate-900">{d.nome}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs border ${TIPO_BADGE[d.tipo] || 'bg-slate-100 text-slate-700'}`}>
                            {d.tipo}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs border ${STATUS_BADGE[d.status] || 'bg-slate-100 text-slate-700'}`}>
                            {d.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {canEditDestinos && (
                            <div className="inline-flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleEdit(d)} data-testid={`edit-destino-${d.id}`}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="outline" className="text-rose-600" onClick={() => handleDelete(d.id)} data-testid={`delete-destino-${d.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar Destino' : 'Novo Destino'}</DialogTitle>
              <DialogDescription>
                Apenas o necessário: nome interno, tipo e URL do WhatsApp.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome do destino *</Label>
                <Input
                  id="nome"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Canal Oficial, Grupo Bairro X"
                  required
                  data-testid="destino-nome-input"
                />
                <p className="text-xs text-slate-500">
                  Identificação interna apenas. Não aparece para os apoiadores.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo *</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(v) => setFormData({ ...formData, tipo: v })}
                >
                  <SelectTrigger data-testid="destino-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposDestino.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="url">URL do WhatsApp *</Label>
                <Input
                  id="url"
                  type="url"
                  value={formData.url_whatsapp}
                  onChange={(e) => setFormData({ ...formData, url_whatsapp: e.target.value })}
                  placeholder="https://chat.whatsapp.com/..."
                  required
                  data-testid="destino-url-input"
                />
                <a
                  href="https://faq.whatsapp.com/673193694148537"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-indigo-600"
                >
                  Como obter o link de um grupo/canal <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger data-testid="destino-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="pausado">Pausado</SelectItem>
                    <SelectItem value="arquivado">Arquivado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="submit-destino-btn">
                  {editingId ? 'Salvar' : 'Criar destino'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

const Kpi = ({ label, value, color = 'text-slate-900' }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4">
    <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-2xl font-black mt-1 tabular-nums ${color}`}>{value}</div>
  </div>
);
